/**
 * Contraste WCAG des couleurs de theme — UPT-1955.
 *
 * Retour UAT 17.09 : « dans marketplace le bouton ACHETER est fond noir texte
 * blanc en clair et fond blanc texte blanc (on ne voit rien) en sombre ».
 *
 * Le bouton prend l'accent du tenant mais la couleur du texte
 * (`primaryForeground`) etait figee dans le theme, sans aucun rapport avec cet
 * accent : sur le tenant Demo (accent #09DB28, texte blanc) le contraste tombe
 * a 1,88:1, tres en dessous du seuil AA de 4,5:1.
 *
 * Ce module derive la couleur d'encre a poser sur un fond donne :
 * noir ou blanc, selon celui qui offre le meilleur contraste. Une valeur fixee
 * explicitement par le theme du tenant est respectee, SAUF si son contraste
 * tombe sous 3:1 — auquel cas elle est corrigee et l'ecart est signalable dans
 * la page de diagnostic.
 *
 * Fonctions pures, sans DOM : les entrees sont des couleurs hexadecimales
 * (`#rgb` ou `#rrggbb`). Les appelants qui manipulent d'autres notations CSS
 * passent d'abord par `css_color_to_hex` (theme_css_variables).
 */

/** Seuil AA pour du texte de taille normale. */
export const wcag_aa_normal_text = 4.5;
/** Seuil AA pour du texte large / des elements non textuels. */
export const wcag_aa_large_text = 3;
/** Seuil AAA pour du texte de taille normale. */
export const wcag_aaa_normal_text = 7;
/**
 * Seuil en deca duquel une valeur explicite du theme est jugee cassee et
 * remplacee par l'encre derivee.
 */
export const theme_ink_override_threshold = wcag_aa_large_text;

export const theme_ink_dark = '#000000';
export const theme_ink_light = '#ffffff';

type RgbTriplet = { r: number; g: number; b: number };

const hex_expression = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

const parse_hex = (value: string | null | undefined): RgbTriplet | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const match = value.trim().match(hex_expression);
  if (!match) {
    return null;
  }

  const raw = match[1];
  const normalized =
    raw.length === 3
      ? raw
          .split('')
          .map((character) => `${character}${character}`)
          .join('')
      : raw;

  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
};

const channel_luminance = (channel: number) => {
  const normalized = channel / 255;
  return normalized <= 0.03928
    ? normalized / 12.92
    : Math.pow((normalized + 0.055) / 1.055, 2.4);
};

/** Luminance relative WCAG 2.x (0 = noir, 1 = blanc). `null` si la couleur est illisible. */
export const relative_luminance = (color: string | null | undefined): number | null => {
  const rgb = parse_hex(color);
  if (!rgb) {
    return null;
  }

  return (
    0.2126 * channel_luminance(rgb.r) +
    0.7152 * channel_luminance(rgb.g) +
    0.0722 * channel_luminance(rgb.b)
  );
};

/** Rapport de contraste WCAG entre deux couleurs (1 a 21). `null` si l'une est illisible. */
export const contrast_ratio = (
  foreground: string | null | undefined,
  background: string | null | undefined
): number | null => {
  const foregroundLuminance = relative_luminance(foreground);
  const backgroundLuminance = relative_luminance(background);
  if (foregroundLuminance === null || backgroundLuminance === null) {
    return null;
  }

  const lighter = Math.max(foregroundLuminance, backgroundLuminance);
  const darker = Math.min(foregroundLuminance, backgroundLuminance);
  return (lighter + 0.05) / (darker + 0.05);
};

/** Noir ou blanc, selon celui qui contraste le mieux avec le fond. */
export const pick_readable_ink = (background: string | null | undefined): string => {
  const darkRatio = contrast_ratio(theme_ink_dark, background);
  const lightRatio = contrast_ratio(theme_ink_light, background);
  if (darkRatio === null || lightRatio === null) {
    return theme_ink_light;
  }
  return darkRatio >= lightRatio ? theme_ink_dark : theme_ink_light;
};

export type ThemeInkResolution = {
  /** Couleur d'encre retenue. */
  color: string;
  /** `explicit` : valeur du theme conservee ; `derived` : valeur calculee. */
  source: 'explicit' | 'derived';
  /** Contraste de la couleur retenue sur le fond. */
  ratio: number | null;
  /** Contraste qu'aurait donne la valeur explicite du theme, si elle existe. */
  explicitRatio: number | null;
  /** Vrai quand la valeur explicite du theme a du etre corrigee. */
  corrected: boolean;
  /** Vrai quand la couleur retenue atteint le seuil AA texte normal. */
  meetsAa: boolean;
};

/**
 * Resout la couleur de texte a poser sur `background`.
 *
 * La valeur explicite du theme est prioritaire tant qu'elle reste lisible
 * (>= 3:1) ; en dessous, elle est remplacee par l'encre derivee et `corrected`
 * passe a vrai pour que la page de diagnostic puisse le signaler.
 */
export const resolve_ink_on_color = (
  background: string | null | undefined,
  explicitForeground?: string | null
): ThemeInkResolution => {
  const derived = pick_readable_ink(background);
  const derivedRatio = contrast_ratio(derived, background);
  const explicitRatio = explicitForeground
    ? contrast_ratio(explicitForeground, background)
    : null;

  if (explicitForeground && explicitRatio !== null && explicitRatio >= theme_ink_override_threshold) {
    return {
      color: explicitForeground,
      source: 'explicit',
      ratio: explicitRatio,
      explicitRatio,
      corrected: false,
      meetsAa: explicitRatio >= wcag_aa_normal_text,
    };
  }

  return {
    color: derived,
    source: 'derived',
    ratio: derivedRatio,
    explicitRatio,
    corrected: Boolean(explicitForeground) && explicitRatio !== null,
    meetsAa: derivedRatio !== null && derivedRatio >= wcag_aa_normal_text,
  };
};
